import React, { useEffect, useRef } from "react";
import type {
  VisitFreshness,
  VisitMetrics,
  VisitQuery,
  VisitsAccess,
} from "../shared/visits.ts";
import { date, Icon } from "./ui.tsx";
import {
  compositionOf,
  formatVisitNumber as format,
} from "./visits-view-model.ts";
import "../docs/design/NUMBER_MOTION.js";

/** Only server results enter the shared animation engine. Intermediate values never feed calculations. */
export function Count({
  value,
  decimals = 0,
  scope,
  label,
}: {
  value: number | null;
  decimals?: number;
  scope: string;
  label: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (value == null || !ref.current) return;
    (window as any).GabineteNumbers?.bind(ref.current, scope);
  }, [value, decimals, scope]);
  return (
    <span
      ref={ref}
      role="img"
      aria-label={
        value == null
          ? `${label}: dado indisponível`
          : `${format(value, decimals)} ${label}`
      }
    >
      {value == null ? (
        <span>—</span>
      ) : (
        <span
          aria-hidden="true"
          data-count-key={label}
          data-count-value={value}
          data-count-decimals={decimals}
          data-count-fractional={decimals > 0 ? "true" : "false"}
          data-count-final={format(value, decimals)}
        >
          {format(value, decimals)}
        </span>
      )}
    </span>
  );
}

export function Freshness({
  value,
  access,
}: {
  value?: VisitFreshness;
  access: VisitsAccess;
}) {
  if (!value) return null;
  const message = {
    ready: "Dados atualizados",
    stale: "Dados anteriores — atualização pendente",
    partial: "Consolidação parcial",
    unavailable: "Dados ainda indisponíveis",
    updating: "Atualização em andamento",
  }[value.state];
  const failed = value.sources.filter((source) => source.state === "failed");
  const hour = value.lastSuccessAt
    ? new Intl.DateTimeFormat("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value.lastSuccessAt))
    : null;
  return (
    <div className={`vo-freshness ${value.state}`}>
      <div className="vo-update-line">
        <span>
          <Icon name={value.state === "ready" ? "check" : "clock"} size={16} />
          {value.state === "ready" && hour ? `Atualizado às ${hour}` : message}
        </span>
        <details className="vo-update-details">
          <summary>Detalhes</summary>
          <div>
            <strong>{message}</strong>
            <p>
              {value.lastSuccessAt
                ? `Última sincronização bem-sucedida: ${date(value.lastSuccessAt)}.`
                : "Nenhuma sincronização concluída."}
            </p>
            <p>
              {value.availableSources} de {value.includedSources} fontes com
              dados disponíveis. Referência: {date(value.asOf)}.
            </p>
            <ul>
              {value.sources.map((source) => (
                <li key={source.sourceId}>
                  <strong>
                    {access.sources.find((item) => item.id === source.sourceId)
                      ?.name || "Fonte não identificada"}
                  </strong>
                  <span>
                    {
                      {
                        never: "Ainda não atualizada",
                        syncing: "Atualizando",
                        ready: "Atualizada",
                        failed: "Falha na atualização",
                      }[source.state]
                    }
                  </span>
                  {source.lastSuccessAt && (
                    <small>Último sucesso: {date(source.lastSuccessAt)}</small>
                  )}
                  {source.error && <small>{source.error}</small>}
                  {source.issues?.length > 0 && (
                    <small>
                      {source.issues.length}{" "}
                      {source.issues.length === 1
                        ? "pendência de mapeamento"
                        : "pendências de mapeamento"}
                    </small>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </details>
      </div>
      {(value.state !== "ready" || failed.length > 0) && (
        <p className="vo-freshness-warning" role="status">
          <Icon name="alert" size={16} />
          <span>
            {value.state === "partial"
              ? "Base parcial: os totais representam somente os dados disponíveis. A cobertura global não está disponível."
              : value.state === "unavailable"
                ? "Não há uma base disponível para esta consulta. Isso não significa zero endereços."
                : value.state === "stale"
                  ? "Os dados são de uma atualização anterior."
                  : value.state === "updating"
                    ? "Atualizando as fontes. Os dados exibidos são da última leitura disponível."
                    : ""}
            {failed.length > 0
              ? ` ${failed.length} ${failed.length === 1 ? "fonte falhou" : "fontes falharam"} na atualização; consulte os detalhes.`
              : ""}
          </span>
        </p>
      )}
    </div>
  );
}

export function VisitSummary({
  metrics,
  freshness,
  loading,
  canOpen,
  onOpen,
  scope,
}: {
  metrics?: VisitMetrics;
  freshness?: VisitFreshness;
  loading: boolean;
  canOpen: boolean;
  onOpen: (changes: Partial<VisitQuery>) => void;
  scope: string;
}) {
  const composition = compositionOf(metrics, freshness?.state);
  const labels = {
    visited: "Visitados",
    pending: "Pendentes",
    analysis: "Em análise",
  } as const;
  return (
    <section
      className="vo-summary"
      aria-label="Resumo de endereços"
      aria-busy={loading}
    >
      <div className="vo-summary-primary">
        <button
          className="vo-total"
          disabled={!canOpen || metrics?.uniqueAddresses == null || loading}
          onClick={() => onOpen({})}
        >
          <span>Endereços únicos</span>
          <strong>
            <Count
              value={metrics?.uniqueAddresses ?? null}
              scope={scope}
              label="endereços únicos"
            />
          </strong>
          <small>
            {loading ? (
              "Consultando…"
            ) : canOpen && metrics?.uniqueAddresses != null ? (
              <>
                Ver endereços <Icon name="arrow" size={14} />
              </>
            ) : (
              "Total no escopo selecionado"
            )}
          </small>
        </button>
        <div className="vo-coverage">
          <strong>
            <Count
              value={metrics?.coverage ?? null}
              decimals={1}
              scope={scope}
              label="% de cobertura"
            />
            {metrics?.coverage != null && <span aria-hidden="true">%</span>}
          </strong>
          <span>Cobertura de visitas</span>
          {metrics?.coverage == null && (
            <small>{loading ? "Consultando…" : "Ainda indisponível"}</small>
          )}
        </div>
      </div>
      {metrics?.visited != null && metrics.uniqueAddresses != null && (
        <p className="vo-coverage-sentence">
          <strong>
            {format(metrics.visited)} de {format(metrics.uniqueAddresses)}
          </strong>{" "}
          endereços visitados
        </p>
      )}
      {composition.state === "ready" && (
        <div
          className="vo-composition"
          role="img"
          aria-label={composition.segments
            .map((s) => `${format(s.value)} ${labels[s.key].toLowerCase()}`)
            .join("; ")}
        >
          {composition.segments
            .filter((s) => s.value > 0)
            .map((s) => (
              <span
                key={s.key}
                className={s.key}
                style={{ flexGrow: s.share }}
              />
            ))}
        </div>
      )}
      <div className="vo-summary-statuses">
        {(["visited", "pending", "analysis"] as const).map((key) => (
          <button
            key={key}
            className={`vo-summary-status ${key}`}
            disabled={!canOpen || metrics?.[key] == null || loading}
            onClick={() => onOpen({ metricStatus: key })}
          >
            <span className="vo-status-label">
              <Icon
                name={
                  key === "visited"
                    ? "check"
                    : key === "analysis"
                      ? "alert"
                      : "clock"
                }
                size={16}
              />
              {labels[key]}
            </span>
            <strong>
              <Count
                value={metrics?.[key] ?? null}
                scope={scope}
                label={`${labels[key].toLowerCase()} no resumo`}
              />
            </strong>
            {canOpen && metrics?.[key] != null && (
              <Icon name="arrow" size={14} />
            )}
          </button>
        ))}
      </div>
      {composition.state === "empty" && (
        <p className="vo-summary-note">Nenhum endereço neste recorte.</p>
      )}
      {!!metrics?.analysis && (
        <p className="vo-summary-note">
          <Icon name="alert" size={17} />
          <span>
            {format(metrics.analysis)}{" "}
            {metrics.analysis === 1 ? "endereço precisa" : "endereços precisam"}{" "}
            de conferência. Em análise faz parte do total, separado de Visitados
            e Pendentes.
          </span>
        </p>
      )}
      {metrics?.provisional && (
        <p className="vo-summary-note">
          <Icon name="info" size={17} />
          <span>
            Indicador provisório. <a href="#vo-method">Como é calculado</a>
          </span>
        </p>
      )}
      {composition.state === "inconsistent" && (
        <p className="vo-summary-note" role="status">
          A composição não está disponível: as situações recebidas precisam de
          conferência. Os valores foram preservados.
        </p>
      )}
    </section>
  );
}
